import { apiFetch } from './client';
import type {
  AccountBrand, AccountSso, BillingBanner, ConnectionChecks, ConsoleBillingFields, DowngradeCheck, FormLogo,
  IdentityProvider, LogoAlign, Plan,
  PlanFeatures, PlanLimits, PlatformRole, StorageForm, TenantPlan,
} from '../shared/types';

export interface AdminUser {
  id: number; email: string; name: string | null; role: string;
  active?: boolean; created_at?: string;
  account_id?: number | null; is_platform_admin?: boolean;
  /** set only for the people who run the service */
  platform_role?: PlatformRole | null;
  /** how this person signs in — see the Users table's Sign-in method column */
  sso_sub?: string | null; sso_provider_id?: number | null;
  sso_exempt?: boolean; last_signin_at?: string | null;
  /** when the password was last set, and by whom — see the Users table */
  password_changed_at?: string | null;
  password_changed_by?: 'self' | 'account_admin' | 'platform' | 'reset_link' | 'invite' | null;
  /** when their invitation was last sent; null = created before invites existed */
  invited_at?: string | null;
  invited_by?: number | null;
}

/**
 * Invited, and not yet arrived.
 *
 * Mirrors invitePending on the server: an invitation has gone out and no
 * password has ever been set, so this login cannot be signed in to by anyone —
 * including whoever created it. Derived from the two timestamps rather than a
 * flag, so the pill and the server can never disagree.
 */
export const isInvitePending = (u: AdminUser): boolean =>
  !!u.invited_at && !u.password_changed_at;

/**
 * What happened to an invitation.
 *
 * `url` is present only when the email could NOT be sent — an install with no
 * outgoing mail has to hand the link over some other way, and the UI shows it
 * for exactly that case. When the mail went out, nobody but the recipient ever
 * sees the link.
 */
export interface SentInvite {
  email: string;
  emailed: boolean;
  url?: string;
  error?: string;
}

/**
 * The two ways to reset somebody else's password.
 *
 * Emailing a link is the better one where mail works: a password an admin
 * typed is a password two people know. Setting one is what an install with no
 * SMTP is left with, and it is honest about that rather than absent.
 */
export type PasswordReset = { sendLink: true } | { password: string };

export interface AccountView {
  id: number; name: string; slug: string; status: string;
  /** the plan's key, joined from the plan row rather than stored on the account */
  plan: string;
}

/**
 * DEPRECATED (040). The open-ended free period, replaced by a bounded one-time
 * credit. The columns survive on the account row so history is recoverable;
 * nothing writes them and no screen reads them.
 */
export interface FreePeriod { free_until: string | null; free_reason: string | null }

export type BillingInterval = 'month' | 'year';

/** What a customer pays, which is not always what their plan lists. */
export interface AccountPricing {
  billing_interval: BillingInterval;
  /** a negotiated price replacing the list price; null = they pay list */
  price_override_cents: number | null;
  price_override_note: string | null;
}

/** The resolved answer to "what are they paying?", computed on the server. */
export interface EffectivePrice {
  cents: number | null;
  interval: BillingInterval;
  currency: string;
  /** the plan's own price, for showing what a negotiated one replaced */
  listCents: number | null;
  overridden: boolean;
  note: string | null;
}

export interface AccountSummary extends AccountView, FreePeriod, AccountPricing {
  /** which plan row — the thing that is written; `plan` is its key */
  plan_id: number;
  plan_name: string;
  user_count: number; form_count: number; submission_count: number; last_submission_at: string | null; created_at: string;
  /** where invoices and dunning notices go; null until someone has set one */
  billing_email: string | null;
  /** read-only sign-in summary for the platform console */
  signIn?: AccountSignIn;
  /**
   * The account's owner as the console names it (074): its earliest active
   * Owner login. Absent from a server older than the account page.
   */
  owner_email?: string | null;
  owner_name?: string | null;
  /**
   * The 30-day soft delete (074): set while the account waits to be deleted
   * — suspended, billing paused — and the status Restore puts back.
   */
  delete_after?: string | null;
  status_before_delete?: string | null;
}

/** Derived per account: team sign-in mode, its provider, and anything failing. */
export interface AccountSignIn {
  mode: string;
  providerName: string | null;
  formProviders: number;
  failingProvider: string | null;
  failingSince: string | null;
}

export interface MeResponse {
  success: boolean;
  admin: AdminUser;
  account: AccountView | null;
  /** the plan features in force, for plan-gated UI; null for platform staff outside an account */
  features: {
    sso: boolean; api: boolean; customBranding: boolean; smartPdf: boolean;
    /** 044/048 — folders, the role ladder and grants, and named groups, as one */
    teamAccess: boolean;
    /** 047 — hide the Form-Magic badge on a published form */
    hideAdvertisement: boolean;
  } | null;
  masquerading: boolean;
  /**
   * How this support visit went in (074): 'read' means every save is refused
   * by the server for the whole visit. Null when not in support mode; absent
   * from an older server, which had only full access.
   */
  masqueradeAccess?: SupportAccess | null;
  isPlatformAdmin: boolean;
  /** which rung of the platform ladder, or null for a customer's user */
  platformRole: PlatformRole | null;
  /** false when this server has no SMTP — no screen may offer to email a link */
  mailConfigured?: boolean;
  /** the platform's public origin for forms, when the console has one (U6 share links) */
  publicBaseUrl?: string | null;
  /**
   * The origin share links build on: the account's live custom domain when
   * it has one (verified, certificate issued, plan still includes it), else
   * the platform origin. Null means "use the browser's own origin".
   */
  formLinkBaseUrl?: string | null;
  /** Owners and Admins by name — the "who can I ask?" every refusal ends on */
  admins?: string[];
  /** how long this session survives with nothing happening on it, for the idle watchdog */
  sessionIdleMs?: number;
  /** whether THIS sign-in has a second factor — never the secret, never the codes */
  twoFactorEnabled?: boolean;
  /** the account-wide policy: everyone here signs in with 2FA */
  twoFactorRequired?: boolean;
  /**
   * The customer-facing rules the service runs on (Platform › Rules): the
   * trial length, the declined-card and deletion graces, the minimum password.
   * Every screen that says one of these numbers reads it here rather than
   * keeping a copy; absent from an older server, whose numbers were the
   * defaults in shared/rules.ts.
   */
  rules?: PublicRules;
}

/** The subset of Platform › Rules a signed-in customer's app, and the public site, may read. */
export interface PublicRules {
  firstTrialDays: number;
  dunningGraceDays: number;
  trialReminderDaysBefore: number;
  deleteGraceDays: number;
  domainsPerAccount: number;
  minPasswordLength: number;
}
export interface MondayBoard { id: string; name: string }
export interface MondayColumn { id: string; title: string; type: string; statusLabels?: Record<string, number> }

export interface LoginResponse {
  success: boolean;
  admin?: AdminUser;
  /** 2FA is on for this sign-in: no session yet — send a code to /login/2fa with this challenge */
  twoFactor?: boolean;
  challenge?: string;
  /** the account requires 2FA and this user has none: signed in only far enough to enroll */
  twoFactorSetup?: boolean;
}

export const adminLogin = (email: string, password: string) =>
  apiFetch<LoginResponse>('/admin/login', { method: 'POST', body: JSON.stringify({ email, password }) });

/** The second half of a 2FA sign-in: the challenge /login answered with, plus the code. */
export const adminLogin2fa = (challenge: string, code: string) =>
  apiFetch<{ success: boolean; admin: AdminUser }>('/admin/login/2fa', {
    method: 'POST', body: JSON.stringify({ challenge, code }),
  });

/**
 * Self-serve signup: creates the account + its first user and signs them in.
 *
 * A paid plan answers with `checkoutUrl` — the account exists and is signed
 * in, but the plan is not theirs until Stripe has the card, so the page sends
 * them there rather than into the app. `checkoutError` means the account was
 * created and the checkout was not; the page says so instead of pretending.
 */
export const signup = (body: {
  company: string; name: string; email: string; password: string;
  planKey?: string; interval?: 'month' | 'year';
}) =>
  apiFetch<{
    success: boolean; admin: AdminUser; checkoutUrl?: string; checkoutError?: string;
  }>('/signup', { method: 'POST', body: JSON.stringify(body) });

// ---- platform console (service owner only) ----
/**
 * The Accounts page's figure tiles, counted by the server over EVERY account
 * (R12) — not the visible rows, which is what the page's sub-line sums.
 * `trials` is a standing free period or a signup trial (`trialing`);
 * `trialsEndingSoon` is those ending within 7 days; `pastDue` is Stripe's
 * word or the account's own status.
 */
export interface AccountsSummary {
  accounts: number;
  mrrCents: number;
  paying: number;
  trials: number;
  trialsEndingSoon: number;
  pastDue: number;
}
/** `summary` is absent from a server older than R12; the tiles render only when it is there. */
export const listAccounts = () =>
  apiFetch<{ success: boolean; accounts: AccountSummary[]; summary?: AccountsSummary }>('/admin/platform/accounts');
export const createAccountApi = (body: Record<string, unknown>) =>
  apiFetch<{
    success: boolean; account: AccountSummary; owner: AdminUser | null;
    /** present when an owner email was given — they are invited, not given a password */
    ownerInvite: SentInvite | null;
  }>('/admin/platform/accounts', { method: 'POST', body: JSON.stringify(body) });
/**
 * What the server did about this account's Stripe discounts, present only on
 * writes that moved money. An agreed price and a comp both reach the customer
 * as coupons, and `applied: false` with a reason is the case staff most need
 * to see: the console changed, the bill did not.
 */
export interface DiscountSync {
  applied: boolean;
  reason: string | null;
  warning: string | null;
  agreed: { amountOffCents: number; netCents: number; couponId: string } | null;
  credit: { amountOffCents: number; days: number; uncoveredCents: number; couponId: string } | null;
  /**
   * The running percent-off (D4, R12): added beside the agreed and credit
   * coupons, never replacing them. `months: null` is forever; `endsAt` is
   * Stripe's own end of the repeating coupon. Absent from writes that do not
   * touch it on an older server.
   */
  percent?: { percentOff: number; months: number | null; couponId: string; endsAt: string | null } | null;
}

export const updateAccountApi = (id: number, body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; account: AccountSummary; discounts?: DiscountSync }>(
    `/admin/platform/accounts/${id}`, { method: 'PUT', body: JSON.stringify(body) });
/**
 * One account as its list row carries it (074) — the account page's head,
 * tabs and tiles. Its money lives in `getAccountBilling`.
 */
export const getAccountApi = (id: number) =>
  apiFetch<{ success: boolean; account: AccountSummary & Partial<ConsoleBillingFields> }>(`/admin/platform/accounts/${id}`);
/** What the server says a deletion or a restore did at Stripe, and who it emailed. */
export type BillingHold = 'paused' | 'resumed' | 'none' | 'not_connected';
/**
 * Delete an account — which SCHEDULES it (074): suspended now, billing paused,
 * permanently deleted after 30 days unless restored.
 */
export const deleteAccountApi = (id: number) =>
  apiFetch<{ success: boolean; account: AccountSummary; deleteAfter: string; stripe: BillingHold; emailed: string[] }>(
    `/admin/platform/accounts/${id}`, { method: 'DELETE' });
/** Call a scheduled deletion off: the old status back, billing resumed. */
export const restoreAccountApi = (id: number) =>
  apiFetch<{ success: boolean; account: AccountSummary; stripe: BillingHold; emailed: string[] }>(
    `/admin/platform/accounts/${id}/restore`, { method: 'POST' });
/** A Super Admin finishes a scheduled deletion now; the name is typed back. */
export const purgeAccountApi = (id: number, confirmName: string) =>
  apiFetch<{ success: boolean }>(`/admin/platform/accounts/${id}/purge`, {
    method: 'POST', body: JSON.stringify({ confirmName }),
  });

/** A note platform staff left on an account (074). Staff-only, append-only. */
export interface StaffNote {
  id: number;
  account_id: number;
  author_id: number | null;
  author_email: string;
  author_name: string | null;
  body: string;
  created_at: string;
}
export const listStaffNotesApi = (id: number) =>
  apiFetch<{ success: boolean; notes: StaffNote[] }>(`/admin/platform/accounts/${id}/notes`);
export const addStaffNoteApi = (id: number, body: string) =>
  apiFetch<{ success: boolean; note: StaffNote }>(`/admin/platform/accounts/${id}/notes`, {
    method: 'POST', body: JSON.stringify({ body }),
  });
export const addAccountUserApi = (id: number, body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; user: AdminUser; invite: SentInvite }>(
    `/admin/platform/accounts/${id}/users`, { method: 'POST', body: JSON.stringify(body) });
/** Every login on one account, for the console's Users dialog. */
export const listAccountUsersApi = (id: number) =>
  apiFetch<{ success: boolean; users: AdminUser[] }>(`/admin/platform/accounts/${id}/users`);
/** Reset any login on the platform — a customer's user or a colleague's. */
export const platformResetPasswordApi = (userId: number, body: PasswordReset) =>
  apiFetch<{ success: boolean; emailedTo?: string; user?: AdminUser }>(
    `/admin/platform/users/${userId}/password`, { method: 'POST', body: JSON.stringify(body) }
  );
/** Send somebody's invitation again — staff, or a customer's user. */
export const platformResendInviteApi = (userId: number) =>
  apiFetch<{ success: boolean; invite: SentInvite }>(
    `/admin/platform/users/${userId}/invite`, { method: 'POST' }
  );
/**
 * How staff enter an account (074): read-only, where the server refuses every
 * save for the visit, or full access as the owner.
 */
export type SupportAccess = 'read' | 'full';
/** Entering support mode: how, and why — the reason is required and lands in both audit logs. */
export interface SupportVisit { access: SupportAccess; reason: string; ref?: string }
export const masqueradeApi = (accountId: number, visit: SupportVisit) =>
  apiFetch<{ success: boolean; account: AccountView; access: SupportAccess }>(
    `/admin/platform/masquerade/${accountId}`, { method: 'POST', body: JSON.stringify(visit) });
export const stopMasqueradeApi = () =>
  apiFetch<{ success: boolean }>('/admin/platform/masquerade/stop', { method: 'POST' });

// ---- platform staff ----
/** The roster, plus the roles this viewer is allowed to hand out. */
export const listPlatformStaff = () =>
  apiFetch<{ success: boolean; staff: AdminUser[]; grantableRoles: PlatformRole[] }>('/admin/platform/staff');
export const createPlatformStaffApi = (body: { email: string; name?: string; platformRole: PlatformRole }) =>
  apiFetch<{ success: boolean; user: AdminUser; invite: SentInvite }>(
    '/admin/platform/staff', { method: 'POST', body: JSON.stringify(body) });
export const setPlatformStaffRoleApi = (id: number, platformRole: PlatformRole) =>
  apiFetch<{ success: boolean; user: AdminUser }>(`/admin/platform/staff/${id}/role`, { method: 'PATCH', body: JSON.stringify({ platformRole }) });
export const setPlatformStaffActiveApi = (id: number, active: boolean) =>
  apiFetch<{ success: boolean }>(`/admin/platform/staff/${id}/active`, { method: 'PATCH', body: JSON.stringify({ active }) });

// ---- audit trail ----

/** A field that moved, as the audit detail records it. */
export interface AuditChange<T = string | null> { from: T; to: T }

/**
 * One thing a platform admin did. Read-only, and never edited after the fact.
 *
 * `account_name` and `target_email` are copies taken when the entry was
 * written, so an entry about a deleted account still says which account.
 */
export interface PlatformAuditEntry {
  key: string;
  actor_id: number | null;
  actor_email: string;
  actor_role: PlatformRole | null;
  action: string;
  account_id: number | null;
  account_name: string | null;
  target_user_id: number | null;
  target_email: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}

/**
 * The audit log page's area chips (R12), each one a slice the server knows:
 * accounts (create, rename, status, delete, logins), billing (plan, price,
 * free periods, credit, billing email, portal link, discount), plans, staff,
 * site, marketing, services (platform.storage / platform.settings) and
 * support (the masquerade rows only).
 */
export type PlatformAuditArea =
  | 'accounts' | 'billing' | 'plans' | 'staff' | 'site' | 'marketing' | 'services' | 'support';

/**
 * The platform trail, newest first. `before` is the opaque `nextCursor` of the
 * page before (keyset on created_at, seq — both halves of the union); the
 * one-argument call the Accounts page makes is unchanged.
 */
export const listPlatformAudit = (
  limit = 50,
  opts: { before?: string | null; area?: PlatformAuditArea | null; accountId?: number | null } = {},
) => {
  const q = new URLSearchParams();
  q.set('limit', String(limit));
  if (opts.area) q.set('area', opts.area);
  if (opts.accountId) q.set('accountId', String(opts.accountId));
  if (opts.before) q.set('before', opts.before);
  return apiFetch<{ success: boolean; entries: PlatformAuditEntry[]; nextCursor?: string | null }>(
    `/admin/platform/audit?${q.toString()}`,
  );
};

// ---- integration catalog ----
export interface CatalogField {
  key: string; label: string; kind: string; required?: boolean; placeholder?: string;
  hint?: string; options?: { value: string; text: string }[]; secret?: boolean; template?: boolean; default?: string | boolean;
  /** Only render this setting when another one holds one of these values. */
  showWhen?: { field: string; is: string[] };
  /** Only render this setting on a form that continues another — it is about the earlier forms' files. */
  continuingOnly?: boolean;
}
export interface CatalogEntry {
  type: string; title: string; icon: string; blurb: string; category: string; docs?: string; fields?: CatalogField[];
  /** Values this integration hands to connections that run after it. */
  provides?: { key: string; label: string }[];
}
export const getIntegrationCatalog = () =>
  apiFetch<{ success: boolean; builtIn: CatalogEntry[]; integrations: CatalogEntry[] }>('/admin/integration-catalog');
// ---- account integration credentials (041) ----
export interface CredentialField {
  key: string; label: string; secret?: boolean; placeholder?: string; hint?: string;
}
export interface CredentialProvider { provider: string; fields: CredentialField[] }
export interface SavedCredential {
  id: number; provider: string; label: string;
  config: Record<string, unknown>;
  /** which secret fields are stored — never their values */
  secretsSet: string[];
  updatedAt?: string;
}
export const listCredentials = () =>
  apiFetch<{ success: boolean; providers: CredentialProvider[]; credentials: SavedCredential[] }>(
    '/admin/credentials');
export const saveCredential = (provider: string, values: Record<string, string>, label = '') =>
  apiFetch<{ success: boolean; credential: SavedCredential }>(`/admin/credentials/${provider}`, {
    method: 'PUT', body: JSON.stringify({ values, label }),
  });
export const deleteCredentialApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/integrations/${id}`, { method: 'DELETE' });

// ---- Settings → Integrations, rebuilt (044) ----

/**
 * One connection type on the Integrations screen.
 *
 * `fields` is null for the nine types that hold nothing at the account level —
 * a Slack webhook URL *is* a channel — and `holdsNothing` is the sentence the
 * screen shows instead. They are listed rather than absent, because a customer
 * who cannot see that a delivery type exists cannot ask for it.
 */
export type ConnectMethod = 'token' | 'oauth';

export interface IntegrationEntry {
  type: string;
  title: string;
  icon: string;
  blurb: string;
  category: string;
  docs: string | null;
  fields: CredentialField[] | null;
  holdsNothing: string | null;
  /** whether the wizard can offer a read-only probe for this one */
  testable: boolean;
  /**
   * How the service is connected (R10, playbook §5): 'token' opens the
   * CredentialWizard; 'oauth' is reserved for a provider sign-in added later.
   * Absent from an older server = 'token'.
   */
  connectMethod?: ConnectMethod;
  credentials: SavedCredential[];
  usage: {
    connections: number;
    forms: number;
    lastFailure: { at: string; error: string } | null;
  };
}

export interface CredentialCheck { ok: boolean; text: string }
export interface CredentialTestResult {
  ok: boolean;
  ms: number;
  target: string;
  checks: CredentialCheck[];
  /** on failure: which setting to change, in words */
  hint?: string;
}

export const listIntegrations = () =>
  apiFetch<{ success: boolean; integrations: IntegrationEntry[] }>('/admin/integrations');

/** Read-only probe. Never throws on a bad credential — that is a result. */
export const testCredentialApi = (provider: string, values: Record<string, string>, label = '') =>
  apiFetch<{ success: boolean; result: CredentialTestResult }>(
    `/admin/integrations/${provider}/test`,
    { method: 'POST', body: JSON.stringify({ values, label }) }
  );

export const saveIntegrationCredential = (provider: string, values: Record<string, string>, label = '') =>
  apiFetch<{ success: boolean; credential: SavedCredential }>(`/admin/integrations/${provider}`, {
    method: 'PUT', body: JSON.stringify({ values, label }),
  });

/** What removing one would cost — asked before the red button is offered. */
export const credentialRemovalApi = (id: number) =>
  apiFetch<{ success: boolean; provider: string; label: string; connections: number; forms: number }>(
    `/admin/integrations/${id}/removal`
  );

export const adminLogout = () => apiFetch<{ success: boolean }>('/admin/logout', { method: 'POST' });
export const adminMe = () => apiFetch<MeResponse>('/admin/me');

// ---- platform storage (platform superadmin) ----
export interface PlatformStorageState {
  source: 'platform' | 'env' | 'disk';
  envConfigured: boolean;
  platform: {
    bucket: string; endpoint: string; region: string;
    accessKeyId: string; hasSecret: boolean; forcePathStyle: boolean;
  } | null;
}
export interface StorageMigrationStatus {
  state: 'idle' | 'running' | 'done' | 'error';
  files: number;
  bytes: number;
  orphans: number;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
}
export interface StorageSettingsBody {
  bucket: string; endpoint?: string; region?: string;
  accessKeyId?: string; secretAccessKey?: string; forcePathStyle?: boolean;
}
export const getPlatformStorage = () =>
  apiFetch<{ success: boolean; storage: PlatformStorageState; migration: StorageMigrationStatus }>('/admin/platform/storage');
export const savePlatformStorage = (body: StorageSettingsBody) =>
  apiFetch<{ success: boolean; storage: PlatformStorageState }>('/admin/platform/storage', { method: 'PUT', body: JSON.stringify(body) });
export const clearPlatformStorage = () =>
  apiFetch<{ success: boolean; storage: PlatformStorageState }>('/admin/platform/storage', { method: 'DELETE' });
export const testPlatformStorage = (body?: StorageSettingsBody) =>
  apiFetch<{ success: boolean; result: { ok: boolean; error?: string } }>('/admin/platform/storage/test', { method: 'POST', body: JSON.stringify(body || {}) });
export const migratePlatformStorage = () =>
  apiFetch<{ success: boolean; started: boolean; migration: StorageMigrationStatus }>('/admin/platform/storage/migrate', { method: 'POST' });

// ---- platform settings: Stripe & SMTP (platform superadmin) ----
export type SettingsSource = 'platform' | 'env' | 'unset';

export interface StripeSettingsState {
  source: SettingsSource;
  envConfigured: boolean;
  platform: {
    keyMode: 'live' | 'test' | null; keyLast4: string; hasWebhookSecret: boolean;
    /** the Connect endpoint's signing secret is saved (absent from a server older than it) */
    hasConnectWebhookSecret?: boolean;
    portalConfigId: string;
  } | null;
  /** R12: the mode of the key in force, env or platform — inferred from the key, never a toggle */
  mode?: 'live' | 'test' | null;
  /**
   * R12: the webhook endpoint and whether events are arriving — true within
   * 48h, false when silent longer, null when none has ever arrived.
   */
  webhook?: { url: string; receivingEvents: boolean | null; lastEventAt: string | null };
  /** R12: Connect — available when a Stripe key is in force; how many accounts connected, how many take payments */
  connect?: { available: boolean; accounts: number; chargesEnabled: number };
}
export interface SmtpSettingsState {
  source: SettingsSource;
  envConfigured: boolean;
  env: { host: string; port: number; user: string; from: string } | null;
  platform: { host: string; port: number; secure: boolean; user: string; hasPass: boolean; from: string } | null;
  /**
   * Tracking (072): the MARKETING bounce rate over the last 30 days — sent is
   * recipients given a Message-ID, bounced is hard bounces — and the 30 days
   * before, for the trend. `ratePct` is null when nothing was sent.
   */
  health?: SmtpHealth;
}
export interface SmtpHealth {
  sent30d: number;
  bounced30d: number;
  ratePct: number | null;
  prevRatePct: number | null;
  trend: 'rising' | 'falling' | 'flat' | null;
}
export interface ProbeResult { ok: boolean; detail?: string; error?: string }

/**
 * Tracking (072): the mailbox bounce notices come back to, read over IMAP
 * (production sends through Google Workspace, so imap.gmail.com). With
 * `useSmtpLogin` the user and password are the SMTP settings in force.
 */
export interface BounceMailboxState {
  configured: boolean;
  platform: {
    host: string; port: number; user: string; useSmtpLogin: boolean; hasPass: boolean; mailbox: string;
  } | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  bounces30d: number;
}
export interface BounceMailboxBody {
  useSmtpLogin: boolean; host: string; port?: number; user?: string; pass?: string; mailbox?: string;
}

export interface StripeSettingsBody { secretKey?: string; webhookSecret?: string; connectWebhookSecret?: string; portalConfigId?: string }
export interface SmtpSettingsBody {
  host: string; port?: number; secure?: boolean; user?: string; pass?: string; from?: string;
}

export interface UrlSettingsState {
  source: SettingsSource;
  envConfigured: boolean;
  effective: { publicBaseUrl: string; appBaseUrl: string };
  platform: { publicBaseUrl?: string; appBaseUrl?: string } | null;
}
/** The links a URL configuration composes — the preview that stands in for a test. */
export interface UrlLinkPreview {
  publicForm: string; magicLink: string; ssoCallback: string;
  uploadedFile: string; stripeWebhook: string; stripeReturn: string;
}

export interface CaptchaSettingsState {
  source: SettingsSource;
  envConfigured: boolean;
  platform: { provider: 'turnstile'; siteKey: string; hasSecret: boolean; updatedAt: string } | null;
}

/** Render (custom domains, T5) — the workspace key that registers customer domains. */
export interface RenderSettingsState {
  source: SettingsSource;
  envConfigured: boolean;
  platform: { serviceId: string; cnameTarget: string; hasApiKey: boolean; updatedAt: string } | null;
}

/** AI form builder — provider, key and model, platform-level like SMTP. */
export interface AiSettingsState {
  source: SettingsSource;
  envConfigured: boolean;
  platform: { provider: 'anthropic'; model: string; hasApiKey: boolean; updatedAt: string } | null;
}

export const getPlatformSettings = () =>
  apiFetch<{
    success: boolean; stripe: StripeSettingsState; smtp: SmtpSettingsState; urls: UrlSettingsState;
    captcha?: CaptchaSettingsState; render?: RenderSettingsState; ai?: AiSettingsState;
    bounceMailbox?: BounceMailboxState;
  }>('/admin/platform/settings');

export const saveAiSettings = (body: { provider: 'anthropic'; model: string; apiKey?: string }) =>
  apiFetch<{ success: boolean; ai: AiSettingsState; detail?: string }>('/admin/platform/settings/ai', { method: 'PUT', body: JSON.stringify(body) });
export const clearAiSettings = () =>
  apiFetch<{ success: boolean; ai: AiSettingsState }>('/admin/platform/settings/ai', { method: 'DELETE' });
export const testAiSettings = (body: { provider?: 'anthropic'; model?: string; apiKey?: string }) =>
  apiFetch<{ success: boolean; result: ProbeResult }>('/admin/platform/settings/ai/test', { method: 'POST', body: JSON.stringify(body) });

export const saveRenderSettings = (body: { serviceId: string; cnameTarget: string; apiKey?: string }) =>
  apiFetch<{ success: boolean; render: RenderSettingsState; detail?: string }>('/admin/platform/settings/render', { method: 'PUT', body: JSON.stringify(body) });
export const clearRenderSettings = () =>
  apiFetch<{ success: boolean; render: RenderSettingsState }>('/admin/platform/settings/render', { method: 'DELETE' });
export const testRenderSettings = (body: { serviceId?: string; cnameTarget?: string; apiKey?: string }) =>
  apiFetch<{ success: boolean; result: ProbeResult }>('/admin/platform/settings/render/test', { method: 'POST', body: JSON.stringify(body) });

export const saveCaptchaSettings = (body: { siteKey: string; secret?: string }) =>
  apiFetch<{ success: boolean; captcha: CaptchaSettingsState; detail?: string }>('/admin/platform/settings/captcha', { method: 'PUT', body: JSON.stringify(body) });
export const clearCaptchaSettings = () =>
  apiFetch<{ success: boolean; captcha: CaptchaSettingsState }>('/admin/platform/settings/captcha', { method: 'DELETE' });
export const testCaptchaSettings = (body: { siteKey?: string; secret?: string }) =>
  apiFetch<{ success: boolean; result: ProbeResult }>('/admin/platform/settings/captcha/test', { method: 'POST', body: JSON.stringify(body) });

export const saveUrlSettings = (body: { publicBaseUrl?: string; appBaseUrl?: string }) =>
  apiFetch<{ success: boolean; urls: UrlSettingsState; links: UrlLinkPreview }>('/admin/platform/settings/urls', { method: 'PUT', body: JSON.stringify(body) });
export const clearUrlSettings = () =>
  apiFetch<{ success: boolean; urls: UrlSettingsState }>('/admin/platform/settings/urls', { method: 'DELETE' });
export const previewUrlSettings = (body: { publicBaseUrl?: string; appBaseUrl?: string }) =>
  apiFetch<{ success: boolean; links: UrlLinkPreview }>('/admin/platform/settings/urls/preview', { method: 'POST', body: JSON.stringify(body) });

export const saveStripeSettings = (body: StripeSettingsBody) =>
  apiFetch<{ success: boolean; stripe: StripeSettingsState; detail?: string }>('/admin/platform/settings/stripe', { method: 'PUT', body: JSON.stringify(body) });
export const clearStripeSettings = () =>
  apiFetch<{ success: boolean; stripe: StripeSettingsState }>('/admin/platform/settings/stripe', { method: 'DELETE' });
export const testStripeSettings = (secretKey?: string) =>
  apiFetch<{ success: boolean; result: ProbeResult }>('/admin/platform/settings/stripe/test', { method: 'POST', body: JSON.stringify(secretKey ? { secretKey } : {}) });

export const saveSmtpSettings = (body: SmtpSettingsBody) =>
  apiFetch<{ success: boolean; smtp: SmtpSettingsState; detail?: string }>('/admin/platform/settings/smtp', { method: 'PUT', body: JSON.stringify(body) });
export const clearSmtpSettings = () =>
  apiFetch<{ success: boolean; smtp: SmtpSettingsState }>('/admin/platform/settings/smtp', { method: 'DELETE' });
export const testSmtpSettings = (body: Partial<SmtpSettingsBody> & { sendTo?: string }) =>
  apiFetch<{ success: boolean; result: ProbeResult }>('/admin/platform/settings/smtp/test', { method: 'POST', body: JSON.stringify(body) });

export const saveBounceMailbox = (body: BounceMailboxBody) =>
  apiFetch<{ success: boolean; bounceMailbox: BounceMailboxState; detail?: string }>('/admin/platform/settings/bounce-mailbox', { method: 'PUT', body: JSON.stringify(body) });
export const clearBounceMailbox = () =>
  apiFetch<{ success: boolean; bounceMailbox: BounceMailboxState }>('/admin/platform/settings/bounce-mailbox', { method: 'DELETE' });
export const testBounceMailbox = (body: Partial<BounceMailboxBody>) =>
  apiFetch<{ success: boolean; result: ProbeResult }>('/admin/platform/settings/bounce-mailbox/test', { method: 'POST', body: JSON.stringify(body) });

// ---- Platform rules: the numbers the service runs on (every rung reads; a Super Admin writes) ----
export type RuleSource = 'platform' | 'env' | 'default';
export interface EditableRule {
  key: string;
  group: string;
  label: string;
  blurb: string;
  unit: string;
  kind: 'int' | 'bool';
  default: number | boolean;
  min?: number;
  max?: number;
  /** every place the value reaches — said beside the field */
  reaches: string[];
  env?: string;
  /** in force now */
  value: number | boolean;
  source: RuleSource;
}
/** A rule that stays in code: its value and the file it lives in. */
export interface FixedRule {
  key: string;
  group: string;
  label: string;
  value: string;
  blurb: string;
  where: string;
  env?: string;
}
export interface RolesLadder {
  roles: { key: string; label: string }[];
  capabilities: { capability: string; role: string }[];
  where: string;
}
export interface RuleChange { key: string; from: number | boolean; to: number | boolean }
export interface PlatformRulesPage {
  success: boolean;
  rules: EditableRule[];
  fixed: FixedRule[];
  roles: { platform: RolesLadder; account: RolesLadder };
  groups: { key: string; label: string }[];
  updatedAt: string | null;
  updatedBy: { id: number | null; email: string } | null;
  /** whether "a paid plan is paid for" runs on this install, and the Stripe key mode it runs against */
  enforcement?: { enforcing: boolean; stripe: 'live' | 'test' | null };
  /** on a save or a reset: what actually moved */
  changes?: RuleChange[];
}
export const getPlatformRules = () => apiFetch<PlatformRulesPage>('/admin/platform/rules');
export const savePlatformRules = (body: Record<string, number | boolean | null>) =>
  apiFetch<PlatformRulesPage>('/admin/platform/rules', { method: 'PUT', body: JSON.stringify(body) });
export const resetPlatformRules = () => apiFetch<PlatformRulesPage>('/admin/platform/rules', { method: 'DELETE' });

// ---- API keys (superadmin) ----
export interface ApiKeySummary {
  id: number;
  name: string;
  token_prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}
export const listApiKeysApi = () =>
  apiFetch<{ success: boolean; apiEnabled: boolean; keys: ApiKeySummary[] }>('/admin/api-keys');
export const createApiKeyApi = (name: string) =>
  apiFetch<{ success: boolean; key: ApiKeySummary; token: string }>('/admin/api-keys', { method: 'POST', body: JSON.stringify({ name }) });
export const revokeApiKeyApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/api-keys/${id}`, { method: 'DELETE' });
/** Is admin-panel SSO enabled? (drives the "Sign in with SSO" button) */
export const adminSsoConfig = () => apiFetch<{ success: boolean; enabled: boolean; loginUrl: string }>('/admin/sso/config');

/**
 * Which Monday.com token a board lookup should use: the token being typed in
 * the editor (sent in the POST body, never a URL), or the one already stored
 * on a saved connection.
 */
export interface MondayAuthRef { connectionId?: number; apiToken?: string }

export const adminMondayBoards = (auth: MondayAuthRef) =>
  apiFetch<{ success: boolean; boards: MondayBoard[] }>('/admin/monday/boards', { method: 'POST', body: JSON.stringify(auth) });
export const adminMondayColumns = (auth: MondayAuthRef, boardId: string) =>
  apiFetch<{ success: boolean; boardName: string; columns: MondayColumn[] }>(
    `/admin/monday/boards/${encodeURIComponent(boardId)}/columns`, { method: 'POST', body: JSON.stringify(auth) });
/** Columns of the board's subitem board (empty + a reason when Monday has not created one). */
export const adminMondaySubitemColumns = (auth: MondayAuthRef, boardId: string) =>
  apiFetch<{ success: boolean; boardName: string; subitemBoardId: string | null; columns: MondayColumn[]; reason?: string }>(
    `/admin/monday/boards/${encodeURIComponent(boardId)}/subitem-columns`, { method: 'POST', body: JSON.stringify(auth) });
export const adminDuplicateBoard = (auth: MondayAuthRef, sourceBoardId: string, name: string) =>
  apiFetch<{ success: boolean; board: MondayBoard; columns: MondayColumn[] }>('/admin/monday/duplicate-board', { method: 'POST', body: JSON.stringify({ ...auth, sourceBoardId, name }) });

/**
 * One person's Access column, as the Users table shows it.
 *
 * `everything` is the Owner/Admin cell ("every form · bypasses grants");
 * otherwise the two counts are real, because 6C quotes them back in the
 * change-role consequence sentence.
 */
export interface UserReachSummary {
  userId: number;
  forms: number;
  folders: number | null;
  everything: boolean;
}

export const adminListUsers = () => apiFetch<{
  success: boolean; users: AdminUser[]; totalForms?: number; reach?: UserReachSummary[];
  /** R10: each person's second factor (on/off) and group names — never the secret */
  facts?: { userId: number; twoFactor: boolean; groups: string[] }[];
}>('/admin/users');
/**
 * Invite somebody into the account. Takes no password — there is nowhere to
 * put one. `invite` is null when the account requires SSO, where there is no
 * password to set and `ssoOnly` says so instead.
 */
export const adminCreateUser = (body: { email: string; name?: string; role?: string }) =>
  apiFetch<{ success: boolean; user: AdminUser; invite: SentInvite | null; ssoOnly: boolean }>(
    '/admin/users', { method: 'POST', body: JSON.stringify(body) });

/** Send it again. Refused once they have set a password — that is a reset. */
export const adminResendInvite = (id: number) =>
  apiFetch<{ success: boolean; invite: SentInvite }>(
    `/admin/users/${id}/invite`, { method: 'POST' });

// ---- single sign-on: the per-account provider library + team sign-in ----
export interface SsoSettingsResponse {
  success: boolean;
  sso: AccountSso;
  /** team sign-in is set to single sign-on, and the account's plan does not include it right now */
  ssoOff?: boolean;
  providers: IdentityProvider[];
  callbackUri: string;
  accountSlug: string;
  /** the page people are sent to: /login/<slug> */
  brandedLoginUrl: string;
  /** the raw start URL, for an IdP tile or an intranet button */
  directLoginUrl: string;
  userCount: number;
  breakGlassCount: number;
  /** Where a new form lands (044, Q3) — everyone in the account, or its maker. */
  newFormAccess?: 'everyone' | 'creator';
  /** T2: whether this account requires 2FA of every password sign-in. */
  twoFactorRequired?: boolean;
}

export const getSsoSettings = () => apiFetch<SsoSettingsResponse>('/admin/settings/sso');
export const saveSsoSettings = (body: Partial<AccountSso>) =>
  apiFetch<{ success: boolean; sso: AccountSso; warnings?: string[] }>(
    '/admin/settings/sso', { method: 'PATCH', body: JSON.stringify(body) });

export const listProvidersApi = () =>
  apiFetch<{ success: boolean; providers: IdentityProvider[] }>('/admin/providers');
export const getProviderApi = (id: number | string) =>
  apiFetch<{ success: boolean; provider: IdentityProvider }>(`/admin/providers/${id}`);
export const createProviderApi = (body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; provider: IdentityProvider }>('/admin/providers', { method: 'POST', body: JSON.stringify(body) });
export const updateProviderApi = (id: number, body: Record<string, unknown>) =>
  apiFetch<{ success: boolean; provider: IdentityProvider }>(`/admin/providers/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteProviderApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/providers/${id}`, { method: 'DELETE' });

/** Endpoints discovery resolved, plus the six-row checklist. */
export interface ResolvedEndpoints {
  issuer?: string;
  authorization_endpoint?: string;
  token_endpoint?: string;
  jwks_uri?: string;
}
export const runProviderChecksApi = (id: number) =>
  apiFetch<{ success: boolean; checks: ConnectionChecks; resolved: ResolvedEndpoints; provider: IdentityProvider }>(
    `/admin/providers/${id}/checks`, { method: 'POST' });

/** Email a one-time sign-in link to someone who has an account at the provider. */
export const sendProviderTestLink = (id: number, email: string) =>
  apiFetch<{ success: boolean; sentTo: string; expiresInMinutes: number }>(
    `/admin/providers/${id}/test-link`, { method: 'POST', body: JSON.stringify({ email }) });

export const setUserSsoExemptApi = (id: number, exempt: boolean) =>
  apiFetch<{ success: boolean; user: AdminUser }>(`/admin/users/${id}/sso-exempt`, { method: 'PATCH', body: JSON.stringify({ exempt }) });

/** Change a user's role or switch them off/on — the Users row menu. */
/** The customer's ladder since 044 — see server/src/lib/accountRoles.ts. */
export type AccountRole = 'owner' | 'admin' | 'member';

export const adminUpdateUser = (id: number, body: { role?: AccountRole; active?: boolean }) =>
  apiFetch<{ success: boolean; user: AdminUser }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** Reset one of this account's users — superadmin only, and never yourself. */
export const adminResetUserPassword = (id: number, body: PasswordReset) =>
  apiFetch<{ success: boolean; emailedTo?: string; user?: AdminUser }>(
    `/admin/users/${id}/password`, { method: 'POST', body: JSON.stringify(body) }
  );

/** Change your own — the current password is what makes it yours to change. */
export const changeMyPassword = (currentPassword: string, newPassword: string) =>
  apiFetch<{ success: boolean }>('/admin/me/password', {
    method: 'POST', body: JSON.stringify({ currentPassword, newPassword }),
  });

// ---- two-factor authentication (T2) ----

/** Start (or restart) enrollment: the otpauth URI for the QR, the secret for typing by hand. */
export const twoFactorSetupApi = () =>
  apiFetch<{ success: boolean; secret: string; uri: string }>('/admin/me/2fa/setup', { method: 'POST' });

/** Prove a code; the recovery codes come back exactly once. */
export const twoFactorEnableApi = (code: string) =>
  apiFetch<{ success: boolean; recoveryCodes: string[] }>('/admin/me/2fa/enable', {
    method: 'POST', body: JSON.stringify({ code }),
  });

/** Turning it off costs both factors — the password and a current (or recovery) code. */
export const twoFactorDisableApi = (password: string, code: string) =>
  apiFetch<{ success: boolean }>('/admin/me/2fa/disable', {
    method: 'POST', body: JSON.stringify({ password, code }),
  });

/** Reset a member's 2FA (lost phone). Clears it, ends their sessions, emails them. */
export const resetUserTwoFactor = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/users/${id}/2fa/reset`, { method: 'POST' });

// ---- the account audit trail (T3) ----

export interface AccountAuditEntry {
  id: number;
  actor_id: number | null;
  actor_email: string;
  action: string;
  target: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}

export const listAccountAuditApi = (opts: { before?: number; actorId?: number; action?: string; limit?: number } = {}) => {
  const q = new URLSearchParams();
  if (opts.before) q.set('before', String(opts.before));
  if (opts.actorId) q.set('actorId', String(opts.actorId));
  if (opts.action) q.set('action', opts.action);
  if (opts.limit) q.set('limit', String(opts.limit));
  const qs = q.toString();
  return apiFetch<{ success: boolean; entries: AccountAuditEntry[]; nextBefore: number | null }>(
    `/admin/audit${qs ? `?${qs}` : ''}`
  );
};

// ---- custom domains (T5) ----

export interface CustomDomain {
  id: number;
  domain: string;
  verify_token: string;
  verified_at: string | null;
  render_status: string | null;
  last_error: string | null;
}

export interface CustomDomainsState {
  success: boolean;
  /** false = this install has no Render credentials; the card says so */
  configured: boolean;
  /** what the customer points their CNAME at */
  cnameTarget: string | null;
  domains: CustomDomain[];
}

export const listCustomDomainsApi = () => apiFetch<CustomDomainsState>('/admin/domains');
export const addCustomDomainApi = (domain: string) =>
  apiFetch<{ success: boolean; domain: CustomDomain }>('/admin/domains', {
    method: 'POST', body: JSON.stringify({ domain }),
  });
export const verifyCustomDomainApi = (id: number) =>
  apiFetch<{ success: boolean; domain: CustomDomain }>(`/admin/domains/${id}/verify`, { method: 'POST' });
export const customDomainStatusApi = (id: number) =>
  apiFetch<{ success: boolean; domain: CustomDomain }>(`/admin/domains/${id}/status`);
export const removeCustomDomainApi = (id: number) =>
  apiFetch<{ success: boolean }>(`/admin/domains/${id}`, { method: 'DELETE' });

// ---- right to erasure (T4) ----

export interface ErasurePreview {
  email: string;
  matches: { formId: number; formName: string; submissions: number }[];
  invites: number;
  /** rows on form invitation batches (the emailed-list feature) */
  invitations: number;
  links: number;
  totalSubmissions: number;
}

/** Whether the form assistant may read this account's submissions (068). Owner/Admin. */
export const aiPrivacyApi = () =>
  apiFetch<{ success: boolean; submissionAccess: boolean }>('/admin/privacy/ai');
export const setAiPrivacyApi = (submissionAccess: boolean) =>
  apiFetch<{ success: boolean; submissionAccess: boolean }>('/admin/privacy/ai', {
    method: 'PATCH', body: JSON.stringify({ submissionAccess }),
  });

/**
 * How long this account's file links open for anybody holding them (finding
 * 96). `mode` is what the account chose; `defaultDays` the platform's number;
 * `enforced` whether Form-Magic has switched the lifetime on yet.
 */
export interface FileLinkSettings {
  mode: 'default' | 'days' | 'never';
  days: number | null;
  defaultDays: number;
  maxDays: number;
  enforced: boolean;
  canChange: boolean;
}
export const fileLinksApi = () =>
  apiFetch<{ success: boolean; fileLinks: FileLinkSettings }>('/admin/privacy/file-links');
export const setFileLinksApi = (choice: { mode: 'default' | 'never' } | { mode: 'days'; days: number }) =>
  apiFetch<{ success: boolean; fileLinks: FileLinkSettings }>('/admin/privacy/file-links', {
    method: 'PATCH', body: JSON.stringify(choice),
  });

/** How many OTHER live sessions this login holds (R10, Profile & security). */
export const mySessionsApi = () =>
  apiFetch<{ success: boolean; others: number }>('/admin/me/sessions');

/** Sign out everywhere else; this tab stays signed in under a fresh session id. */
export const revokeOtherSessionsApi = () =>
  apiFetch<{ success: boolean; ended: number }>('/admin/me/sessions/revoke-others', { method: 'POST' });

/** The audit log as a CSV download, with the page's own filters (R10). */
export const auditExportUrl = (opts: { actorId?: number; action?: string } = {}) => {
  const q = new URLSearchParams();
  if (opts.actorId) q.set('actorId', String(opts.actorId));
  if (opts.action) q.set('action', opts.action);
  const qs = q.toString();
  return `/api/admin/audit/export.csv${qs ? `?${qs}` : ''}`;
};

/** One erasure this account ran, read back from its audit entry (R10, read-only). */
export interface ErasureRequest {
  id: number; email: string | null; submissions: number; invites: number;
  links: number; childrenScrubbed: number; by: string; at: string;
}
export const privacyRequestsApi = (before?: number) =>
  apiFetch<{ success: boolean; requests: ErasureRequest[]; nextBefore: number | null }>(
    `/admin/privacy/requests${before ? `?before=${before}` : ''}`
  );

export const erasurePreviewApi = (email: string) =>
  apiFetch<{ success: boolean; preview: ErasurePreview }>('/admin/privacy/erasure/preview', {
    method: 'POST', body: JSON.stringify({ email }),
  });

/** `confirm` must repeat the address — the server refuses anything else. */
export const eraseRespondentApi = (email: string, confirm: string) =>
  apiFetch<{ success: boolean; submissions: number; invites: number; invitations: number; links: number; childrenScrubbed: number }>(
    '/admin/privacy/erasure', { method: 'POST', body: JSON.stringify({ email, confirm }) }
  );

/** The account-wide "everyone signs in with 2FA" policy (teamAccess-gated). */
export const setTwoFactorRequired = (required: boolean) =>
  apiFetch<{ success: boolean; twoFactorRequired: boolean }>('/admin/settings/sso/two-factor', {
    method: 'PATCH', body: JSON.stringify({ required }),
  });

// ---- forgotten passwords (public: whoever needs these cannot sign in) ----

/**
 * Ask for a reset link. Always answers the same way whether or not the address
 * is a login here — the response is read by whoever typed it, so confirming
 * would make the form a way to find out who has an account.
 */
export const forgotPassword = (email: string) =>
  apiFetch<{ success: true; sent: true }>('/auth/forgot-password', {
    method: 'POST', body: JSON.stringify({ email }),
  });

/**
 * Is the link still good? Asked before a password field is shown, not after.
 *
 * Answers for both kinds that end at a password field — a reset and an
 * invitation — and says which arrived, because the page that greets a brand
 * new colleague should not open with "choose a NEW password".
 */
export const checkResetToken = (token: string) =>
  apiFetch<{
    success: true; valid: true; kind: 'reset' | 'invite';
    email: string; name?: string | null; ttlMinutes: number;
    /** the minimum the new password must meet (Platform › Rules); absent from an older server */
    minPassword?: number;
    /** An invitation only: the workspace, who added them and as what — what
     *  the invitation email already told them (R13's "Join {account}"). */
    accountName?: string | null; invitedBy?: string | null; role?: string | null;
  }>(`/auth/reset-password/${encodeURIComponent(token)}`);

export const resetPassword = (token: string, password: string) =>
  apiFetch<{ success: true; kind: 'reset' | 'invite'; email: string }>('/auth/reset-password', {
    method: 'POST', body: JSON.stringify({ token, password }),
  });

/**
 * Which provider, if any, an email domain signs in with. Public and answered
 * from the domain alone, so the sign-in page can offer the right button before
 * anyone has proved who they are.
 */
/** One provider a signed-out person can be sent to. */
export interface SsoChoice {
  name: string;
  slug: string;
  loginUrl: string;
}

export interface SsoResolution {
  success: boolean;
  ssoRequired: boolean;
  accountSlug: string | null;
  accountName?: string;
  /** providers[0], repeated for the shape the page used before lists existed */
  provider: (SsoChoice & { accountName: string }) | null;
  providers: SsoChoice[];
}

export const resolveSso = (email: string) =>
  apiFetch<SsoResolution>(`/sso/resolve?email=${encodeURIComponent(email)}`);

/** What /login/:accountSlug draws itself from. 404s for a password-only account. */
export const accountSsoPublic = (accountSlug: string) =>
  apiFetch<{
    success: boolean; accountName: string; accountSlug: string;
    ssoRequired: boolean; providers: SsoChoice[];
  }>(`/sso/${encodeURIComponent(accountSlug)}/public`);

// ---- the customer's own billing ----

/** Every metric the limits cover, from the same counters the checks read. */
export interface UsageFull {
  forms: number;
  users: number;
  submissions_this_month: number;
  storage_bytes: number;
  submission_records: number;
  /** successful AI generations this month (065); absent from older servers */
  ai_generations?: number;
  /** answered form-assistant questions this month (068); absent from older servers */
  ai_assistant_messages?: number;
}

/** The Stripe mirror as fields — never raw customer/subscription ids. */
export interface BillingSubscription {
  status: string | null;
  currentPeriodEnd: string | null;
  graceUntil: string | null;
  billingInterval: BillingInterval;
  billingEmail: string | null;
  hasStripeCustomer: boolean;
  /** read from Stripe — every account has a Customer, not every account a card */
  hasPaymentMethod: boolean;
  hasSubscription: boolean;
}

/** The active free period, named for the banner and the plan card's pill. */
export interface BillingTrial {
  planKey: string;
  planName: string;
  expiresAt: string;
  autoConvert: boolean;
  /** the trialled plan's price at the account's interval — what converting charges (R11) */
  amountCents?: number | null;
  interval?: BillingInterval;
  currency?: string;
  /**
   * Where a period that does not convert goes back to (D3): the restore point
   * the grant recorded, named and priced. Null on rows older than it.
   */
  previousPlan?: { key: string; name: string; cents: number | null; currency: string; interval: BillingInterval } | null;
  /**
   * A period that does not convert while Stripe holds a subscription for it:
   * the subscription ends with the period and the account moves to Free.
   */
  endsSubscription?: boolean;
}

/** The default card's face (R11) — brand, last four, expiry; never the number. */
export interface CardSummary {
  brand: string;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
}

/** What the account may DO right now — trial or dunning included. */
export interface BillingEntitlement {
  plan: { key: string; name: string };
  limits: PlanLimits;
  features: PlanFeatures;
  trial: BillingTrial | null;
}

/**
 * A plan move Stripe is holding until the current period ends.
 *
 * Read live from the Stripe schedule on every load — there is no local record
 * of a pending change, so a page that does not render this cannot tell "we
 * scheduled it" from "the change never reached Stripe".
 */
export interface ScheduledChange {
  planKey: string | null;
  planName: string;
  interval: 'month' | 'year' | null;
  amountCents: number | null;
  currency: string;
  effectiveAt: string | null;
  /** the phase points at a price no plan claims — Stripe was edited directly */
  unknownPlan: boolean;
}

/** What Settings → Billing renders. */
export interface BillingView {
  account: {
    id: number; name: string; status: string; planId: number;
    creditDays: number | null; creditReason: string | null;
  };
  plan: Plan;
  price: EffectivePrice;
  /** the catalogue a customer may see, always including their own plan */
  plans: Plan[];
  entitlement: BillingEntitlement;
  trial: BillingTrial | null;
  usage: UsageFull;
  subscription: BillingSubscription;
  invoices: Invoice[];
  /** false = no Stripe key on the server, so no invoice can exist yet */
  billingConnected: boolean;
  stripeMode: 'live' | 'test' | null;
  /** what this becomes at the next renewal, null when nothing is pending */
  scheduledChange: ScheduledChange | null;
  /** forms held read-only after an involuntary drop, named so the page can say which */
  readOnlyForms?: { id: number; name: string }[];
  /** the default card's face; null when there is none or the default is not a card (R11) */
  paymentMethod?: CardSummary | null;
  /** the free days a first paid plan starts with, while unspent — once per account */
  trialOffer?: { days: number } | null;
  /**
   * A running percent-off (D4) — staff set it; the customer sees it as a
   * Discount row where an agreed price would be named. One discount at a
   * time (Ben, 2026-09-26), so an account with this has no agreed price.
   * `months: null` and `endsAt: null` are forever. Null once it has ended;
   * absent from an older server.
   */
  percentOff?: PercentOff | null;
}

/** A percent-off as the customer's pages name it: "15% off every invoice until 3 October 2027". */
export interface PercentOff { percent: number; months: number | null; endsAt: string | null }

/** Mirrored from Stripe when it lands; empty until then. */
export interface Invoice {
  id: number;
  /** the Stripe number, or the literal 'upcoming' for the scheduled row */
  number: string | null;
  plan_name: string;
  amount_cents: number;
  currency: string;
  status: string;
  /** >1 on a paid invoice means "failed, then paid" */
  attempt_count?: number;
  period_start: string | null;
  period_end: string | null;
  hosted_url: string | null;
  receipt_url: string | null;
  created_at: string;
}

export const getBilling = () =>
  apiFetch<{ success: boolean } & BillingView>('/admin/billing');
/** Older name, kept so nothing that already imports it moves. */
export const getBillingApi = getBilling;

/** The banner state, for any account admin — derived fresh on every load. */
export const getBillingBanner = () =>
  apiFetch<{ success: boolean } & BillingBanner>('/admin/billing/banner');

/** The picker's catalogue: public non-retired plans plus the caller's own. */
export const getPlansCatalog = () =>
  apiFetch<{ success: boolean; plans: TenantPlan[]; currentPlanId: number }>('/plans');

/** The gate, for rendering — the mutation re-runs it server-side regardless. */
export const getDowngradeCheck = (planKey: string) =>
  apiFetch<{ success: boolean; check: DowngradeCheck }>(
    `/admin/billing/downgrade-check?planKey=${encodeURIComponent(planKey)}`);

/**
 * Start a Checkout Session for a FIRST paid plan; the browser goes to the
 * returned URL. An account that already has a subscription is refused (409
 * `has_subscription`) — it moves up with `upgradePlan` instead.
 */
export const createCheckout = (planKey: string, interval: BillingInterval) =>
  apiFetch<{ success: boolean; url: string }>('/admin/billing/checkout', {
    method: 'POST', body: JSON.stringify({ planKey, interval }),
  });

/**
 * The four shapes an upgrade of a live subscription takes (stripeService):
 * a proration billed now with the renewal unchanged, a new annual term from
 * today, a plan switch inside the first-plan trial, or the end of a free
 * period staff granted.
 */
export type UpgradeKind = 'prorate' | 'new_annual_term' | 'trial_switch' | 'end_free_period';

/** What confirming an upgrade will do — Stripe's own preview, never our arithmetic. */
export interface UpgradeQuote {
  kind: UpgradeKind;
  planKey: string;
  planName: string;
  /** the interval billed from today */
  interval: BillingInterval;
  currency: string;
  dueTodayCents: number;
  lines: { description: string; amountCents: number }[];
  nextAmountCents: number | null;
  nextBillingAt: string | null;
  /** annual → monthly: the renewal at which the monthly price takes over */
  monthlyFrom: string | null;
  agreedPriceEndsCents: number | null;
  /**
   * A plan change ends the percent-off the way it ends the agreed price (Ben,
   * 2026-09-26), so the dialog says so before the confirm. Absent from an
   * older server.
   */
  percentEnds?: PercentOff | null;
  card: CardSummary | null;
  needsCard: boolean;
  /** pinned so the charge is the number quoted */
  prorationDate: number | null;
}

export const quoteUpgrade = (planKey: string, interval: BillingInterval) =>
  apiFetch<{ success: boolean; quote: UpgradeQuote }>('/admin/billing/upgrade/quote', {
    method: 'POST', body: JSON.stringify({ planKey, interval }),
  });

/**
 * Make the upgrade the quote described. A card that will not take the charge
 * is a 402 with Stripe's sentence, and nothing has changed.
 */
export const upgradePlan = (planKey: string, interval: BillingInterval, prorationDate: number | null) =>
  apiFetch<{ success: boolean; kind: UpgradeKind; chargedCents: number; warning: string | null }>(
    '/admin/billing/upgrade', {
      method: 'POST', body: JSON.stringify({ planKey, interval, prorationDate }),
    });

/** A Billing Portal session — cards, receipts and tax detail live there. */
export const createPortalSession = () =>
  apiFetch<{ success: boolean; url: string }>('/admin/billing/portal', { method: 'POST' });

/** The account's own billing address. Empty clears it. */
export const setBillingEmail = (billingEmail: string) =>
  apiFetch<{ success: boolean; billingEmail: string | null }>('/admin/billing/email', {
    method: 'PATCH', body: JSON.stringify({ billingEmail }),
  });

/**
 * What a downgrade ended on the way (Ben, 2026-09-26): a plan change ends the
 * agreed price and the percent-off the moment it is made; a pending credit
 * stays, because it is owed. Only the downgrade route answers it — cancel
 * moves to Free, and the discounts go when the account lands there.
 */
export interface DiscountsEnded {
  agreedCents: number | null;
  percent: { percent: number; months: number | null } | null;
}

/** How a downgrade or cancel resolves: scheduled, or refused with the gate. */
export type DowngradeResult =
  | { ok: true; scheduled: boolean; planKey: string; effectiveAt: string | null; discountsEnded: DiscountsEnded | null }
  | { ok: false; check: DowngradeCheck };

/**
 * The two gated mutations use fetch directly rather than apiFetch: a 409
 * carries the DowngradeCheck the page must render, and apiFetch keeps only
 * the error string.
 */
async function gatedPost(path: string, body?: unknown): Promise<DowngradeResult> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (res.status === 409 && data?.error === 'downgrade_blocked' && data.check) {
    return { ok: false, check: data.check as DowngradeCheck };
  }
  if (!res.ok) throw new Error((data && (data.error || data.message)) || `Request failed (${res.status})`);
  return {
    ok: true, scheduled: !!data.scheduled, planKey: data.planKey, effectiveAt: data.effectiveAt ?? null,
    discountsEnded: data.discountsEnded ?? null,
  };
}

export const downgrade = (planKey: string) => gatedPost('/admin/billing/downgrade', { planKey });
export const cancelPlan = () => gatedPost('/admin/billing/cancel');

/** Per-form upload bytes — the gate's delete_files affordance reads this. */
export const getStorage = () =>
  apiFetch<{ success: boolean; forms: StorageForm[]; totalBytes: number; accountBytes?: number }>('/admin/billing/storage');

/**
 * One invoice's PDF, streamed from Stripe by our server (R11) — a plain link,
 * so the browser downloads it with the session cookie and no script.
 */
export const invoicePdfHref = (invoiceId: number) => `/api/admin/billing/invoices/${invoiceId}/pdf`;

/** Delete one form's uploads. Irreversible; the section two-clicks it. */
export const deleteFormUploads = (formId: number) =>
  apiFetch<{ success: boolean; deletedBytes: number; totalBytes: number }>(
    `/admin/billing/storage/${formId}`, { method: 'DELETE' });

// ---- Payments (Stripe Connect — collecting money on the account's forms) ----
export interface ConnectStatus {
  configured: boolean;
  connected: boolean;
  chargesEnabled: boolean;
  accountId: string | null;
  /** read from the connected account on this visit (R11); absent when nothing was read */
  details?: {
    displayName: string | null;
    payoutsEnabled: boolean;
    payoutInterval: string | null;
    defaultCurrency: string | null;
  };
}

/** One payment taken on a form, read live from the connected account (R11). */
export interface ConnectPayment {
  /** the Checkout Session id — what a refund names */
  id: string;
  createdAt: string;
  amountCents: number;
  currency: string;
  refundedCents: number;
  refundableCents: number;
  status: 'paid' | 'partially_refunded' | 'refunded';
  respondentName: string | null;
  respondentEmail: string | null;
  formId: number | null;
  formName: string | null;
  submissionDbId: number | null;
  /** the response's public id, when it is still in one of this account's forms */
  submissionId: string | null;
}

export interface ConnectMonth {
  collected: { currency: string; cents: number }[];
  refunded: { currency: string; cents: number }[];
  count: number;
  truncated: boolean;
}

export interface RecentPayments {
  /** false: nothing connected, or Stripe could not be read (`warning` says which) */
  available: boolean;
  payments: ConnectPayment[];
  month: ConnectMonth | null;
  formsTakingPayment: { id: number; name: string }[];
  warning?: string;
}

export type RefundReason = 'requested_by_customer' | 'duplicate' | 'fraudulent';
export const getPaymentsStatus = () =>
  apiFetch<{ success: boolean; status: ConnectStatus; warning?: string }>('/admin/payments');
export const startPaymentsConnect = () =>
  apiFetch<{ success: boolean; url: string }>('/admin/payments/connect', { method: 'POST' });
export const disconnectPayments = () =>
  apiFetch<{ success: boolean }>('/admin/payments/connect', { method: 'DELETE' });
/** The tiles and the recent payments, read from the connected account (R11). */
export const getRecentPayments = () =>
  apiFetch<{ success: boolean } & RecentPayments>('/admin/payments/recent');
/** Refund a form payment on the connected account. No amount = everything still refundable. */
export const refundPayment = (body: {
  sessionId: string; amountCents?: number; reason?: RefundReason | null; note?: string; idempotencyKey?: string;
}) => apiFetch<{
  success: boolean;
  refund: { id: string; amountCents: number; currency: string; status: string | null };
  payment: ConnectPayment;
}>('/admin/payments/refunds', { method: 'POST', body: JSON.stringify(body) });

// ---- Account branding (the house style every form inherits) ----

/** What the Branding page loads: the brand, and what a save to it would move. */
export interface BrandingState {
  brand: AccountBrand;
  /** Every form the account has. */
  formCount: number;
  /** How many of them inherit at least one key — the confirm's number. */
  inheritingCount: number;
  /** Whether the branded half (logos, typeface, mode) may be EDITED. */
  customBranding: boolean;
}

export const getBranding = () =>
  apiFetch<{ success: boolean } & BrandingState>('/admin/branding');

/**
 * Save the brand. The logo row is deliberately not part of this — it moves
 * with its bytes through the two routes below, so there is one way a stored
 * file and the brand that points at it can get out of step, and it is none.
 */
export const saveBranding = (brand: AccountBrand) =>
  apiFetch<{ success: boolean; brand: AccountBrand }>('/admin/branding', {
    method: 'PUT', body: JSON.stringify({ brand }),
  });

export const uploadBrandLogo = (file: File, align?: LogoAlign) => {
  const body = new FormData();
  if (align) body.append('align', align);
  body.append('logo', file);
  return apiFetch<{ success: boolean; logo: FormLogo; logos: FormLogo[] }>(
    '/admin/branding/logo', { method: 'POST', body });
};

/**
 * Move one logo to another position. Answers with the whole row, like the
 * upload and the delete — the brand's logo row always comes back from the
 * server rather than being patched hopefully on this side.
 */
export const moveBrandLogo = (url: string, align: LogoAlign) =>
  apiFetch<{ success: boolean; logos: FormLogo[] }>('/admin/branding/logo', {
    method: 'PATCH', body: JSON.stringify({ url, align }),
  });

/** With a url, that one logo; without, every logo the brand has. */
export const deleteBrandLogo = (url?: string) =>
  apiFetch<{ success: boolean; logos: FormLogo[] }>(
    `/admin/branding/logo${url ? `?url=${encodeURIComponent(url)}` : ''}`, { method: 'DELETE' });

/** One form that still answers a key the brand could answer. */
export interface BrandOverrideForm {
  id: number;
  name: string;
  status: string;
  /** Which theme keys it sets — 'accent', 'logos', 'font'… */
  overrides: string[];
}

export const getBrandOverrides = () =>
  apiFetch<{ success: boolean; forms: BrandOverrideForm[] }>('/admin/branding/overrides');

/** Reset many forms to the account branding, all or nothing. */
export const resetFormsToBranding = (formIds: number[]) =>
  apiFetch<{ success: boolean; reset: number }>('/admin/branding/reset', {
    method: 'POST', body: JSON.stringify({ formIds }),
  });
